import type { ReactElement } from "react";
import { Button, Glyph, logical } from "../ui/kit/index.ts";
import { say } from "../words.ts";
import { EntryScreen } from "./entry-screen.tsx";
import { maintenancePage, type MaintenanceTone, type ServerView } from "./server-status.ts";
import { timeOfDay } from "./server-strip.tsx";

/**
 * The maintenance page (ADR-078 decision 7; the design's `ui_kits/surfaces/ServerSurfaces.jsx`, `Maintenance`, a visual
 * reference): shown in place of the log in form on a fresh visit while maintenance runs, and from the strip's Details.
 * The entry layout's backdrop and wordmark over a window "Server status" 300 wide: the headline in the pixel face in
 * its tone's colour, one sentence, and a time strip (a glyph, when the server should be back, and how long that is).
 * While the server is closed the page offers nothing to press: the poll asks in the background, and a player who
 * wants an answer at once reloads the page. Once the server is open again the foot holds Log in, which reloads the
 * page, so the player goes on with the page the server now serves and never the one that waited out the maintenance.
 * The design's "Checks again in 30 s." with Check now, its service rows, its status page link and its Discord
 * sentence are not built (ADR-078 decision 8 and its amendment of 2026-10-06).
 */

/**
 * The page's measurements in logical pixels: the window's width, the gaps between its blocks, the headline's gap
 * to its sentence, the time strip's padding and gap, the footer's gap, and how far the glyph and the running text
 * sit below the top of the time strip's pixel line, so each is set down its middle on a whole pixel.
 */
export const MAINTENANCE_METRICS = { window: 300, gap: 6, headGap: 2, stripPaddingY: 4, stripPaddingX: 5, stripGap: 6, footGap: 4, glyphTop: 2, leftTop: 1 } as const;

/**
 * Each headline tone's colour: brass while planned, warning while running longer, positive once open.
 */
const TONE_COLOURS: Readonly<Record<MaintenanceTone, string>> = { planned: "var(--ot-brass-100)", longer: "var(--ot-warning)", open: "var(--ot-positive)" };

/**
 * The maintenance page.
 *
 * @param props The server's `view`, and `onLogIn`, which takes the player back in once the server is open.
 * @returns The page.
 */
export function MaintenanceScreen({ view, onLogIn }: { readonly view: ServerView; readonly onLogIn: () => void }): ReactElement {
  const page = maintenancePage(view, Date.now(), timeOfDay);
  const metrics = MAINTENANCE_METRICS;
  return (
    <EntryScreen mode="maintenance" layout={`maintenance:${page.tone}`} title={say("status.pageTitle")} width={metrics.window} strip={false}>
      <div className="ot-maintenance" data-maintenance={page.tone} style={{ gap: logical(metrics.gap) }}>
        <div className="ot-maintenance__head" style={{ gap: logical(metrics.headGap) }}>
          <h2 className="ot-maintenance__title" data-maintenance="title" style={{ color: TONE_COLOURS[page.tone] }}>
            {page.title}
          </h2>
          {page.text === null ? null : <p className="ot-maintenance__text">{page.text}</p>}
        </div>
        <div className="ot-maintenance__time" data-maintenance="time" style={{ gap: logical(metrics.stripGap), padding: `${logical(metrics.stripPaddingY)} ${logical(metrics.stripPaddingX)}` }}>
          <span className="ot-maintenance__glyph" style={{ marginTop: logical(metrics.glyphTop), color: page.open ? "var(--ot-positive)" : page.tone === "longer" ? "var(--ot-warning)" : "var(--ot-info)" }}>
            <Glyph name={page.open ? "check" : "clock"} />
          </span>
          <span className="ot-maintenance__when">{page.when}</span>
          {page.left === null ? null : (
            <span className="ot-maintenance__left" style={{ marginTop: logical(metrics.leftTop) }}>
              {page.left}
            </span>
          )}
        </div>
        {page.open ? (
          <div className="ot-maintenance__foot" style={{ gap: logical(metrics.footGap) }}>
            <Button variant="primary" onClick={onLogIn}>
              {say("status.logIn")}
            </Button>
          </div>
        ) : null}
      </div>
    </EntryScreen>
  );
}
